'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { useIconDirection } from './direction.js';
import { isDirectionalIcon, resolveIcon } from './registry.js';
/**
 * ════════════════════════════════════════════════════════════════════════════
 *  <Icon> — the only way to draw one
 * ════════════════════════════════════════════════════════════════════════════
 *
 * ── THE VARIANT RULE, AS CODE ───────────────────────────────────────────────
 *
 * Linear for navigation and inline use; **Bold only for an active or toggled
 * state**. A comment saying so would be obeyed until the first hurry. So there
 * is no `variant` prop and no way to reach one: the raw Iconsax components are
 * sealed inside `iconsax.ts` (guarded by lint and by
 * `test/iconsax-import-guard.test.ts`), and the only lever exposed here is
 * `active`, which is a *state*, not a style. Writing the wrong thing means
 * writing `active` on something that is not active — a claim a reviewer can
 * check, unlike `variant="Bold"`.
 *
 * ── THE GRID ────────────────────────────────────────────────────────────────
 *
 * Iconsax draws on a 24 grid. `size` is a union of the ladder, so `size={17}`
 * — which lands strokes on half-pixels — does not typecheck.
 *
 * Below 24 the glyph is scaled down and its 1.5 stroke goes down with it: at
 * 16px it renders 1.0 CSS px and the icon reads noticeably lighter than the
 * text beside it. `--bk-icon-scale` compensates, so the stroke stays 1.5 at
 * every size under 24 and keeps Iconsax's native weight at 24 and above. The
 * arithmetic has to happen in CSS because Iconsax's `strokeWidth` prop is a
 * no-op — every `<path>` carries its own presentation attribute, and only a CSS
 * rule outranks that. See `src/styles/icons.css`, which the panels get through
 * `@bookora/ui/styles.css` and the other two surfaces import as
 * `@bookora/ui/icons.css`.
 *
 * ── ACCESSIBILITY ───────────────────────────────────────────────────────────
 *
 * Decorative by default — an icon next to its own label is noise to a screen
 * reader. Pass `label` only when the icon IS the accessible name (a bare icon
 * button), and then it renders `role="img"` with that name.
 */
/** The size ladder. Every step keeps the 24 grid on whole or half pixels. */
export const ICON_SIZES = [16, 20, 24, 32, 40, 48];
/** Iconsax's native stroke, on its native grid. */
const NATIVE_STROKE_GRID = 24;
function strokeScaleFor(size) {
    // Compensate only downwards. Above the native grid a heavier rendered stroke
    // is correct — that is what makes a 40px illustration icon read as one.
    return size < NATIVE_STROKE_GRID ? NATIVE_STROKE_GRID / size : 1;
}
export const Icon = forwardRef(function Icon({ name, active = false, size = 24, color = 'currentColor', className, style, label, dir, 'data-testid': testId, }, ref) {
    const direction = useIconDirection(dir);
    const Glyph = resolveIcon(name, direction);
    const variant = active ? 'Bold' : 'Linear';
    const decorative = label === undefined;
    const scale = strokeScaleFor(size);
    const mergedStyle = {
        ...style,
        '--bk-icon-scale': scale,
    };
    return (_jsx(Glyph, { ref: ref, 
        // All three are passed on every render on purpose: Iconsax declares them
        // through `defaultProps`, which React 19 ignores, and the patient PWA is
        // React 19.
        variant: variant, size: size, color: color, className: className ? `bk-icon ${className}` : 'bk-icon', style: mergedStyle, role: decorative ? undefined : 'img', "aria-hidden": decorative ? true : undefined, "aria-label": label, focusable: "false", "data-icon": name, "data-variant": variant, "data-dir": isDirectionalIcon(name) ? direction : undefined, "data-testid": testId }));
});
/**
 * The same icon as an element rather than a component.
 *
 * AntD takes icons as `icon={<X />}` on `Button`, `Menu.Item`, `Tabs.TabPane`
 * and `Result`. Without this, every panel call site spells out `<Icon name=… />`
 * inline — which is fine until someone reaches for `@ant-design/icons` because
 * it was one import shorter. This makes the registry the shortest path.
 */
export function icon(name, props) {
    return _jsx(Icon, { name: name, ...props });
}
//# sourceMappingURL=icon.js.map